import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Sparkles } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../../lib/bookingStatus";
import { CollapsibleSection } from "../CollapsibleSection";
import { useAdminChecklistPin } from "../AdminChecklistPinDialog";

/**
 * Feb 2026 · Team visibility of the booked services (extras + inclusive
 * opt-outs) for the current or next absence. Read-only for customer data,
 * but the team can tick each service as "erledigt" to feed the progress
 * timeline.
 *
 * 24 Jun 2026 · User-Directive „Concierge-Checkliste ohne Sofort-PIN":
 *   Früher feuerte jeder Klick sofort einen PATCH ohne PIN-Header → der
 *   Concierge bekam bei jeder Änderung den 403-Toast „PIN erforderlich, um
 *   die Buchungs-Checkliste zu bearbeiten." Jetzt werden alle Änderungen —
 *   genau wie im Admin-Buchungsmodal — lokal gepuffert und erst beim
 *   finalen „Speichern" mit EINER PIN-Eingabe (persönliche Team-PIN)
 *   gemeinsam persistiert.
 *
 * Extrahiert aus `TeamCustomerDetail.jsx` (Refactor Feb 2026).
 */

const STD_TEAM_SUBCAT_LABEL = {
  interior:   "Innenbereich",
  outdoor:    "Außenbereich",
  management: "Verwaltung",
};
const STD_TEAM_SUBCAT_ORDER = ["interior", "outdoor", "management"];

export function BookingServicesPanel({ booking, onChanged }) {
  const { user } = useAuth() || {};
  const isTeam = user?.role === "admin" || user?.role === "concierge";
  const {
    getPinHeaders: getTeamPinHeaders,
    dialog: teamPinDialog,
  } = useAdminChecklistPin({ enabled: isTeam });

  const [extras, setExtras] = useState([]);
  const [stdServices, setStdServices] = useState([]);
  // Lokale Arbeitskopie der erledigten Leistungen (gepuffert). Der Server-
  // Stand ist `booking.completed_services` — beim finalen Speichern gleichen
  // wir die Differenz ab.
  const [completed, setCompleted] = useState(new Set(booking?.completed_services || []));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/extras"), api.get("/standard-services")])
      .then(([e, s]) => { if (mounted) { setExtras(e.data || []); setStdServices(s.data || []); } })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setCompleted(new Set(booking?.completed_services || []));
  }, [booking?.booking_id, booking?.completed_services]);

  if (!booking) return null;

  const services = booking.services || [];
  const serviceNotes = booking.service_notes || {};
  const disabledKeys = booking.disabled_standard_services || [];
  const disabledSet = new Set(disabledKeys);
  const bookingId = booking.booking_id || booking.id;

  const extraById = Object.fromEntries(extras.map((e) => [e.id, e]));
  const bookedExtras = services.map((id) => extraById[id]).filter(Boolean);

  const stdBySubcategory = { interior: [], outdoor: [], management: [] };
  for (const s of stdServices || []) {
    const sub = (s.subcategory || "management").toLowerCase();
    (stdBySubcategory[sub] || stdBySubcategory.management).push(s);
  }

  // Server-Basislinie + gepufferte Differenz (welche Keys wurden lokal
  // umgeschaltet und müssen beim Speichern gepatcht werden).
  const baseline = new Set(booking?.completed_services || []);
  const dirtyKeys = [];
  for (const k of new Set([...completed, ...baseline])) {
    if (completed.has(k) !== baseline.has(k)) dirtyKeys.push(k);
  }

  const toggleDone = (key) => {
    if (!bookingId || saving) return;
    // Nur lokal puffern — kein Netzwerk-Call, keine PIN-Abfrage beim Klick.
    setCompleted((cur) => {
      const n = new Set(cur);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const handleDiscard = () => setCompleted(new Set(booking?.completed_services || []));

  const handleSave = async () => {
    if (dirtyKeys.length === 0 || saving) return;
    // Einmalige PIN-Eingabe (persönliche Team-PIN) für ALLE gepufferten
    // Änderungen — analog zum Admin-Buchungsmodal.
    const headers = await getTeamPinHeaders();
    if (headers === null) {
      toast.error("Änderungen nicht gespeichert — PIN-Eingabe abgebrochen.");
      return;
    }
    setSaving(true);
    try {
      for (const key of dirtyKeys) {
        await api.patch(
          `/team/bookings/${bookingId}/completed-services`,
          { service_key: key, done: completed.has(key) },
          { headers },
        );
      }
      toast.success(`${dirtyKeys.length} Änderung${dirtyKeys.length === 1 ? "" : "en"} gespeichert.`);
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const ErledigtToggle = ({ id, disabled, testKey }) => {
    const done = completed.has(id);
    return (
      <button
        type="button"
        onClick={() => !disabled && toggleDone(id)}
        disabled={disabled || saving}
        aria-pressed={done}
        aria-label={done ? "Als offen markieren" : "Als erledigt markieren"}
        data-testid={`team-erledigt-toggle-${testKey}${done ? "-done" : ""}`}
        className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center border transition-colors ${
          disabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer"
        }`}
        style={done
          ? { background: STATUS_COLORS.green, borderColor: STATUS_COLORS.green }
          : { background: BRAND_COLORS.white, borderColor: withAlpha(BRAND_COLORS.navy, 0.35) }}
      >
        {done
          ? <Check className="w-3 h-3" style={{ color: BRAND_COLORS.ivory }} strokeWidth={3} />
          : null}
      </button>
    );
  };

  const totalTogglable = (stdServices.filter((s) => !disabledSet.has(s.key)).length) + bookedExtras.length;
  const doneCount = Array.from(completed).filter((k) => {
    // valid = one of the enabled std services OR one of the booked extras
    const stdOk = stdServices.some((s) => s.key === k && !disabledSet.has(s.key));
    const extraOk = bookedExtras.some((e) => e.id === k);
    return stdOk || extraOk;
  }).length;
  const allServicesDone = totalTogglable > 0 && doneCount === totalTogglable;

  return (
    <>
    <div className="mb-8 bg-white border border-navy/10 p-5 md:p-6" data-testid="booking-services-panel">
      <CollapsibleSection
        allDone={allServicesDone}
        count={`${doneCount} / ${totalTogglable} erledigt`}
        testid="booking-services-collapse"
        title={
          <div className="flex items-start gap-2">
            <Sparkles className="w-5 h-5 shrink-0 mt-0.5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
            <div className="flex-1 min-w-0">
              <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Leistungen dieser Abwesenheit</div>
              <div className="text-[12.5px] text-navy-dark/60">
                Klicken Sie den grünen Kreis, um eine Leistung als erledigt zu markieren — bestätigen Sie danach unten mit Ihrer PIN.
              </div>
            </div>
          </div>
        }
      >
      {/* Zusatzleistungen (gebucht) */}
      {bookedExtras.length > 0 && (
        <div className="mb-6" data-testid="team-booking-extras">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/80">
              Gebuchte Zusatzleistungen
            </div>
            <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50">{bookedExtras.length}</div>
          </div>
          <ul className="space-y-2">
            {bookedExtras.map((ex) => {
              const note = serviceNotes[ex.id];
              return (
                <li
                  key={ex.id}
                  className="flex items-start gap-3 bg-ivory/50 border-l-2 border-gold pl-3 pr-3 py-2"
                  data-testid={`team-booking-extra-${ex.key}`}
                >
                  <div className="pt-0.5">
                    <ErledigtToggle id={ex.id} testKey={`extra-${ex.key}`} />
                  </div>
                  <div className="flex-1 min-w-0 text-[13px] text-navy leading-snug">
                    <div className={completed.has(ex.id) ? "text-navy-dark/60 line-through decoration-navy-dark/30" : ""}>
                      {ex.name_de || ex.name_en}
                    </div>
                    {note && (
                      <div
                        className="mt-1 text-[11.5px] italic text-navy-dark/70"
                        data-testid={`team-booking-extra-note-${ex.key}`}
                      >
                        „{note}"
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Inklusiv-Leistungen (vollständige Liste, 3-Sub-Gruppen) */}
      {stdServices.length > 0 && (
        <div data-testid="team-booking-inclusive">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/80">
              Inklusiv-Leistungen
            </div>
            <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50">
              {stdServices.length - disabledKeys.length} / {stdServices.length} aktiv
            </div>
          </div>
          <div className="space-y-4">
            {STD_TEAM_SUBCAT_ORDER.map((sub) => {
              const list = stdBySubcategory[sub];
              if (!list || list.length === 0) return null;
              const enabledCount = list.filter((s) => !disabledSet.has(s.key)).length;
              const doneInGroup = list.filter((s) => completed.has(s.key)).length;
              return (
                <div key={sub} data-testid={`team-inclusive-group-${sub}`}>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70">
                      {STD_TEAM_SUBCAT_LABEL[sub]}
                    </div>
                    <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/45">
                      {doneInGroup} / {enabledCount} erledigt
                    </div>
                  </div>
                  <ul className="space-y-1.5">
                    {list.map((s) => {
                      const isDisabled = disabledSet.has(s.key);
                      const isDone = completed.has(s.key);
                      const note = serviceNotes[s.key];
                      return (
                        <li
                          key={s.key}
                          className="flex items-start gap-3 text-[13px]"
                          data-testid={`team-inclusive-item-${s.key}${isDisabled ? "-disabled" : ""}`}
                        >
                          <div className="pt-0.5">
                            <ErledigtToggle id={s.key} disabled={isDisabled} testKey={`inclusive-${s.key}`} />
                          </div>
                          <div className="flex-1 min-w-0 leading-snug">
                            <div className={isDisabled
                              ? "text-navy-dark/50 line-through decoration-navy-dark/30"
                              : isDone
                              ? "text-navy-dark/60 line-through decoration-navy-dark/25"
                              : "text-navy-dark"}>
                              {s.name_de || s.name_en}
                              {isDisabled && (
                                <span className="ml-2 text-[10.5px] tracking-widest uppercase" style={{ color: STATUS_COLORS.red }}>
                                  vom Kunden abgewählt
                                </span>
                              )}
                            </div>
                            {note && (
                              <div
                                className="mt-0.5 text-[11.5px] italic text-navy-dark/70"
                                data-testid={`team-inclusive-note-${s.key}`}
                              >
                                „{note}"
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </CollapsibleSection>
    </div>

    {/* Feb 2026 · Gepufferter Speichern-Balken (nur Team, nur bei Änderungen).
        Eine PIN-Eingabe bestätigt alle gepufferten Toggles gemeinsam. */}
    {isTeam && dirtyKeys.length > 0 && (
      <div
        className="fixed left-1/2 -translate-x-1/2 bottom-4 z-[60] flex items-center gap-3 flex-wrap px-4 py-3 bg-white shadow-xl border"
        style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.6) }}
        data-testid="booking-services-save-bar"
      >
        <span className="text-[12.5px] text-navy-dark/80" data-testid="booking-services-save-count">
          {dirtyKeys.length} Änderung{dirtyKeys.length === 1 ? "" : "en"}
        </span>
        <button
          type="button"
          onClick={handleDiscard}
          disabled={saving}
          data-testid="booking-services-discard-btn"
          className="text-[11px] tracking-[0.15em] uppercase text-navy-dark/60 hover:text-navy px-3 py-2 disabled:opacity-50"
        >
          Verwerfen
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="booking-services-save-btn"
          className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
          style={{ background: BRAND_COLORS.navy }}
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
          Speichern
        </button>
      </div>
    )}
    {teamPinDialog}
    </>
  );
}
